import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Warning, ArrowRight, SpinnerGap, Eye, EyeSlash } from '@phosphor-icons/react';

const API_URL =
  typeof window !== 'undefined' && window.location.hostname &&
  !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL;

/**
 * Invite — opens via /invite/<token>. Looks up the token, prompts the user
 * for a password, creates the account, and routes them to the dashboard.
 */
export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Validate token on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/invite/${token}`);
        if (!alive) return;
        setEmail(res.data.email || '');
      } catch (err) {
        if (alive) setTokenError(err.response?.data?.detail || 'Invalid invite');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setSubmitError('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(
        `${API_URL}/api/auth/invite/${token}/accept`,
        { password, name: name.trim() || null },
        { withCredentials: true },
      );
      // Successful — cookies are set, route to dashboard
      navigate('/dashboard');
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not create account — try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400" data-testid="invite-loading">
            <SpinnerGap size={18} className="animate-spin" /> Verifying invite…
          </div>
        ) : tokenError ? (
          <div className="rounded-3xl bg-white text-slate-900 p-7 shadow-2xl text-center" data-testid="invite-error">
            <div className="inline-flex w-12 h-12 rounded-full bg-red-50 text-red-600 items-center justify-center mb-3">
              <Warning size={26} weight="fill" />
            </div>
            <h1 className="text-xl font-bold">This invite link is no longer valid</h1>
            <p className="mt-2 text-sm text-slate-600">{tokenError}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-5 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            >
              Back to home
            </button>
          </div>
        ) : (
          <div className="rounded-3xl bg-white text-slate-900 p-7 sm:p-8 shadow-2xl" data-testid="invite-form">
            <div className="inline-flex w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-3">
              <CheckCircle size={26} weight="fill" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">You're approved 🎉</h1>
            <p className="mt-2 text-sm text-slate-600">
              Set a password to finish creating your LifeAdmin account.
            </p>
            <p className="mt-4 text-xs uppercase font-bold tracking-widest text-slate-500">Email</p>
            <p className="text-sm font-semibold text-slate-900">{email}</p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                data-testid="invite-name"
              />
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  minLength={6}
                  placeholder="Choose a password (min 6 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-11 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  data-testid="invite-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Toggle password visibility"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>

              {submitError && (
                <p className="text-xs text-red-600" data-testid="invite-submit-error">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={submitting || password.length < 6}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white py-3 text-[15px] font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="invite-submit"
              >
                {submitting ? (
                  <>
                    <SpinnerGap size={16} className="animate-spin" /> Creating your account…
                  </>
                ) : (
                  <>
                    Create account & sign in <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </button>
              <p className="text-[11px] text-slate-400 text-center">
                Or sign in with Google using {email} — also works.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
